import React from "react";
import { ArrowRight } from "lucide-react";
import { useModals } from "@/lib/modal-context";

const CARDS = [
    {
        label: "This is where your family gathers.",
        sub: "Warm lights. Premium finishes. A space that feels like success.",
        img: "https://images.unsplash.com/photo-1556909114-44e3e9399a2c?auto=format&fit=crop&w=1200&q=80",
        size: "lg:row-span-2 lg:col-span-6 min-h-[420px] lg:min-h-[640px]",
    },
    {
        label: "End your day in calm.",
        sub: "Soft textures. Peaceful lighting. Hotel-like comfort.",
        img: "https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&w=900&q=80",
        size: "lg:col-span-3 min-h-[260px] lg:min-h-[300px]",
    },
    {
        label: "Where mornings feel premium.",
        sub: "Clean design. Smart storage. Effortless functionality.",
        img: "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=900&q=80",
        size: "lg:col-span-3 min-h-[260px] lg:min-h-[300px]",
    },
    {
        label: "Your entire home. Designed like a story.",
        sub: "Everything connects — materials, lighting, flow, energy.",
        img: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1400&q=80",
        size: "lg:col-span-6 min-h-[260px] lg:min-h-[320px]",
    },
];

function FeelCard({ c }) {
    return (
        <div className={`relative overflow-hidden rounded-3xl card-lift ${c.size}`}>
            <img src={c.img} alt={c.label} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="relative z-10 flex h-full flex-col justify-end p-6 sm:p-8 text-white">
                <div className="font-display text-2xl sm:text-3xl font-bold leading-tight max-w-md">{c.label}</div>
                <div className="mt-2 text-sm text-white/85 max-w-md">{c.sub}</div>
            </div>
        </div>
    );
}

export default function FeelGallery() {
    const { openConnect } = useModals();

    return (
        <section id="projects" className="bg-white py-16 sm:py-24">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
                <div className="rounded-[2rem] border border-black/5 bg-white p-5 sm:p-8 lg:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end fade-up">
                        <h2 className="lg:col-span-8 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                            Don&apos;t imagine your home.
                            <br />
                            <span className="text-[#CCFF00]">Feel it.</span>
                        </h2>
                        <p className="lg:col-span-4 text-sm text-black/55 lg:text-right max-w-md lg:ml-auto">
                            Every room is designed to feel personal, premium and practical — a space where your family can live, relax, gather and grow.
                        </p>
                    </div>

                    {/* Asymmetric grid */}
                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 lg:auto-rows-[300px] gap-5">
                        {CARDS.map((c, i) => (
                            <FeelCard key={i} c={c} />
                        ))}
                    </div>

                    {/* Bottom CTA bar */}
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-5">
                        <div className="lg:col-span-8 rounded-3xl bg-[#111111] p-7 sm:p-9 text-white">
                            <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight max-w-2xl">
                                You&apos;re not buying interiors. You&apos;re buying a feeling.
                            </h3>
                            <p className="mt-3 text-sm text-white/70 max-w-2xl">
                                Most homeowners struggle to explain what they want. We simplify it. Pick a vibe, connect instantly, and we guide you from idea → execution.
                            </p>
                        </div>
                        <div className="lg:col-span-4 rounded-3xl bg-[#CCFF00] p-7 sm:p-9 text-black flex flex-col justify-between">
                            <div className="font-display text-5xl sm:text-6xl font-extrabold leading-none">30s</div>
                            <button
                                onClick={() => openConnect({ source: "feel_gallery" })}
                                data-testid="feel-start-dream"
                                className="mt-6 inline-flex items-center gap-2 text-sm font-bold"
                            >
                                Start My Dream <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
